<template>
	<b-modal v-model="isModalVisible" size="md" dialog-class="mt-md-5 p-0 rounded-5" content-class="p-2" @cancel="resetModal"
		footer-class="border-top-0" model-class="modal-custom" header-class="border-bottom-0">

		<!-- HEADER -->
		<template v-slot:modal-header>
			<h5 class="mb-0 text-uppercase font-weight-bold">
				{{ $t('page.twoFactorAuth.2faModalTitle') }}
			</h5>
		</template>

		<!-- BODY -->
		<template v-slot:default>
			<div class="text-center">
				<!-- Instructions -->
				<div class="mb-4">
					<p class="mb-2 font-weight-bold label">{{ $t('page.twoFactorAuth.2faModalInstructions') }}</p>
					<small class="text-muted">
						{{ $t('page.twoFactorAuth.enable2FaStep1') }}<br>
						{{ $t('page.twoFactorAuth.enable2FaStep2') }}<br>
						{{ $t('page.twoFactorAuth.enable2FaStep3') }}
					</small>
				</div>

				<!-- QR Code -->
				<div class="mb-4">
					<div v-if="qrCodeDataUrl" class="d-inline-block p-3 border rounded">
						<img :src="qrCodeDataUrl" alt="2FA QR Code" class="img-fluid" style="max-width: 200px;">
					</div>
					<div v-else-if="isGeneratingQR" class="d-inline-block p-3 border rounded"
						style="width: 200px; height: 200px;">
						<div class="d-flex align-items-center justify-content-center h-100">
							<b-icon icon="arrow-clockwise" animation="spin-pulse" font-scale="4" variant="primary"></b-icon>
						</div>
					</div>
					<div v-else-if="!hasValidTotpData" class="d-inline-block p-3 border rounded text-muted"
						style="width: 200px; height: 200px;">
						<div class="d-flex align-items-center justify-content-center h-100">
							<small>{{ $t('page.twoFactorAuth.2faModalWaitingForData') }}</small>
						</div>
					</div>
					<div v-else class="d-inline-block p-3 border rounded text-danger" style="width: 200px; height: 200px;">
						<div class="d-flex align-items-center justify-content-center h-100">
							<small>{{ $t('page.twoFactorAuth.2faModalQRError') }}</small>
						</div>
					</div>
				</div>

				<!-- Manual Entry Option -->
				<div class="mb-4">
					<details class="border rounded p-2">
						<summary class="text-primary" style="cursor: pointer;">
							<small>{{ $t('page.twoFactorAuth.2faModalManualSetup') }}</small>
						</summary>
						<div class="mt-2">
							<small class="text-muted d-block mb-2">
								{{ $t("page.twoFactorAuth.manualInstruction") }}
							</small>
							<div class="row">
								<div class="col-sm-3"><small><strong>{{ $t("account") }}:</strong></small></div>
								<div class="col-sm-9"><code class="small">{{ totp?.issuer || 'Emotiva' }}</code></div>
							</div>
							<div class="row">
								<div class="col-sm-3"><small><strong>{{ $t("key") }}:</strong></small></div>
								<div class="col-sm-9">
									<code class="user-select-all bg-light p-1 rounded small">{{ secretKey }}</code>
								</div>
							</div>
						</div>
					</details>
					<div v-if="!qrCodeDataUrl && hasValidTotpData" class="mt-2 text-center">
						<b-button size="sm" variant="outline-primary" @click="generateQRCode">
							{{ $t('2faModalRetryQR') || 'Retry QR Code' }}
						</b-button>
					</div>
				</div>

				<!-- Verification Input -->
				<div class="mb-3">
					<p class="font-weight-bold label">{{ $t('page.twoFactorAuth.2faModalVerificationLabel') }}</p>
					<b-form-input v-model="verificationCode" maxlength="6"
						class="text-center" style="letter-spacing: 0.2em; font-size: 1.1em;" @input="validateCode"
						@keyup.enter="submit">
					</b-form-input>
					<small v-if="!totpError" class="text-muted"></small>
					<small v-else>{{ $t(`${totpError}`) }}</small>
				</div>
			</div>
		</template>

		<!-- FOOTER -->
		<template v-slot:modal-footer="{ cancel }">
			<div class="footer-align-right">
				<b-button variant="white" class="mr-3 action-btn justify-content-center align-items-center bold btn-blue-border"
					@click="cancel">
					{{ $t("button.cancel") }}
				</b-button>
				<b-button variant="none" style="color:#fff"
					class="justify-content-center align-items-center bold emotiva-pink-background action-btn"
					:disabled="isFormInvalid" @click="verifyTOTP">
					{{ $t("button.proceed") }}
				</b-button>
			</div>
		</template>

	</b-modal>
</template>

<script>
import QRCode from 'qrcode'

export default {
	props: {
		modalStatus: {
			type: Boolean,
			required: true,
		},
		totp: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			qrCodeDataUrl: null,
			verificationCode: '',
			isGeneratingQR: false,
			wrongTotpMessage: ''

		}
	},
	computed: {
		isModalVisible: {
			get() {
				return this.modalStatus;
			},
			set(modalStatus) {
				this.$emit("setModalStatus", modalStatus);
			},
		},
		isFormInvalid() {
			return !this.verificationCode || this.verificationCode.length !== 6 || !/^\d{6}$/.test(this.verificationCode);
		},
		secretKey() {
			// Handle different possible data structures
			if (this.totp?.secret?.base32) {
				return this.totp.secret.base32;
			} else if (this.totp?.secret) {
				return this.totp.secret;
			}
			return '';
		},
		totpError() {
			return this.wrongTotpMessage || false
		},
		userEmail(){
			return this.$store.getters["auth/getUserAccount"].registration?.email
		},
		totpUrl() {
			if (!this.totp) return '';

			try {
				const email = this.userEmail
				console.log("email: ",email)
				// Build the otpauth URL from the TOTP object properties
				const issuer = this.totp.issuer || 'emotiva';
				const label = email || 'Foresight-user';
				const secret = this.totp.secret?.base32 || this.totp.secret || '';
				const algorithm = this.totp.algorithm || 'SHA1';
				const digits = this.totp.digits || 6;
				const period = this.totp.period || 30;

				if (!secret) {
					console.error('No secret found in TOTP object');
					return '';
				}

				// Construct the otpauth URL
				const params = new URLSearchParams({
					issuer: issuer,
					secret: secret,
					algorithm: algorithm,
					digits: digits.toString(),
					period: period.toString()
				});

				const url = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(label)}?${params.toString()}`;
				return url;

			} catch (error) {
				console.error('Error building TOTP URL:', error);
				return '';
			}
		},
		hasValidTotpData() {
			const url = this.totpUrl;
			return url && typeof url === 'string' && url.length > 0 && url.startsWith('otpauth://');
		}
	},
	watch: {
		modalStatus(newVal) {
			if (newVal && !this.qrCodeDataUrl && this.hasValidTotpData) {
				this.generateQRCode();
			}
		},
		totp: {
			handler(newVal) {
				if (newVal && this.modalStatus && this.hasValidTotpData) {
					this.generateQRCode();
				}
			},
			deep: true,
			immediate: true
		}
	},
	methods: {
		async generateQRCode() {
			if (!this.hasValidTotpData) {
				return;
			}


			this.isGeneratingQR = true;
			try {
				// Generate QR code as data URL
				this.qrCodeDataUrl = await QRCode.toDataURL(this.totpUrl, {
					errorCorrectionLevel: 'M',
					type: 'image/png',
					quality: 0.92,
					margin: 1,
					color: {
						dark: '#000000',
						light: '#FFFFFF'
					},
					width: 200
				});
			} catch (error) {
				console.error('Error generating QR code:', error);
				this.$emit('qr-error', error);
			} finally {
				this.isGeneratingQR = false;
			}
		},

		validateCode() {
			// Remove any non-numeric characters
			this.verificationCode = this.verificationCode.replace(/\D/g, '');

			// Limit to 6 digits
			if (this.verificationCode.length > 6) {
				this.verificationCode = this.verificationCode.substring(0, 6);
			}
		},

		resetModal(){
			this.verificationCode = '';
			this.wrongTotpMessage = '';
			this.qrCodeDataUrl=null;
			this.isGeneratingQR=false;
		},

		submit() {
			if (this.isFormInvalid) return;

			this.$emit("submit", {
				code: this.verificationCode,
				secret: this.secretKey
			});

			// Reset modal
			this.resetModal();
		},

		async verifyTOTP() {
			// const secret = data.secret
			this.$store.commit("loader/SET_LOADER", {});
			this.$store.dispatch('auth/verifyTOTP', this.verificationCode)
				.then(() => {
					this.submit();
					this.$emit("twoFactorIsSet",'enabled')
					this.$emit('setModalStatus',false)
				})
				.catch((error) => {
					console.error("Error verifying TOTP: ", error);
					this.wrongTotpMessage = 'page.twoFactorAuth.2FAWrongTOPT'
				})
				.finally(() => {
					this.$store.dispatch("loader/close");
				});
		}
	},

	beforeDestroy() {
		// Clean up data URL to prevent memory leaks
		if (this.qrCodeDataUrl) {
			URL.revokeObjectURL(this.qrCodeDataUrl);
		}
	}
}
</script>

<style scoped>
.footer-align-right {
	display: flex;
	justify-content: flex-end;
}

.action-btn {
	border-radius: 26px;
	height: 46px;
	min-width: 100px;
}

.btn-blue-border {
	border: 2px solid #cbd2e0;
	border-color: #cbd2e0;
}

.emotiva-pink-background {
	background-color: var(--emotiva-pink);
}

.label {
	color: var(--emotiva-text-color);
}

.user-select-all {
	user-select: all;
	-webkit-user-select: all;
	-moz-user-select: all;
	-ms-user-select: all;
}

code {
	word-break: break-all;
}
</style>